import { Box, Button, ButtonBase, Collapse, Grid, ListSubheader, Typography } from '@material-ui/core';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import React, { useEffect, useMemo, useState } from 'react';
import { useProgramManagementContext } from '../ProgramManagementContext';
import { SideNavHeader } from './ProgramManagementSideNavHeader';
import { ProgramsList } from './ProgramsList';
import { ProgramGroupsReorderList } from './ProgramGroupsReorderList';
import { buildProgramLayout, withSuspendedPrograms } from './program-layout';
import { ProgramLayout } from '../program-management-actions';
import { useUser } from '../../../../global/auth/useUser';
import { getUserIdentityService } from '../../../../global/auth/UserIdentityService';
import { useEnterpriseLock } from '../../../../global/auth/use-enterprise-lock/use-enterprise-lock';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';

const SIDE_NAV_WIDTH = 340;

interface Props {
  pagePadding?: number;
}

const ProgramManagementSideNav = (props: Props) => {
  const { pagePadding } = props;
  const { programs, programGroups, compact, runReorderPrograms } = useProgramManagementContext();
  const { theme } = useCampfireTheme();

  const [searchInput, setSearchInput] = useState<string>();
  const [showActivePrograms, setShowActivePrograms] = useState(true);
  const [showSuspendedPrograms, setShowSuspendedPrograms] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const [pendingLayout, setPendingLayout] = useState<ProgramLayout>();

  useEffect(() => {
    setPendingLayout(undefined);
  }, [programs, programGroups]);

  const { userIdentity } = useUser();
  const { isVmAdmin } = getUserIdentityService(userIdentity);
  const locked = useEnterpriseLock();

  const activePrograms = useMemo(
    () =>
      programs.filter(
        (program) =>
          program.dateSuspended === null &&
          (searchInput && !reorderMode ? program.name.toLowerCase().includes(searchInput.toLowerCase()) : true)
      ),
    [programs, searchInput, reorderMode]
  );

  const savedLayout = useMemo(() => buildProgramLayout(activePrograms, programGroups), [activePrograms, programGroups]);
  const layout = pendingLayout ?? savedLayout;
  const hasBlankHeading = layout.groups.some((group) => group.name.trim() === '');

  const handleSaveOrder = () => {
    if (pendingLayout) {
      runReorderPrograms(
        withSuspendedPrograms(
          {
            ...pendingLayout,
            groups: pendingLayout.groups.map((group) => ({ ...group, name: group.name.trim() })),
          },
          programs.filter((program) => program.dateSuspended !== null)
        )
      ).then((saved) => {
        if (!saved) setPendingLayout(undefined);
      });
    }
    setReorderMode(false);
  };

  const handleCancelReorder = () => {
    setPendingLayout(undefined);
    setReorderMode(false);
  };

  const suspendedPrograms = useMemo(
    () =>
      programs.filter(
        (program) =>
          program.dateSuspended !== null &&
          (searchInput ? program.name.toLowerCase().includes(searchInput.toLowerCase()) : true)
      ),
    [programs, searchInput]
  );

  return (
    <Grid
      item
      container
      style={{
        overflow: 'hidden auto',
        display: 'flex',
        minWidth: pagePadding ? SIDE_NAV_WIDTH - 2 * pagePadding : SIDE_NAV_WIDTH,
        maxWidth: compact ? 'none' : SIDE_NAV_WIDTH,
        flexDirection: 'column',
      }}
    >
      <SideNavHeader setSearchInput={setSearchInput} />

      <Grid item style={{ display: 'flex', flex: 1, width: '100%', overflow: 'hidden auto' }}>
        <Box style={{ width: '100%', paddingBottom: '16px' }}>
          <Grid container direction='column' spacing={1} style={{ width: '100%', margin: 0 }}>
            <Box display='flex' alignItems='center'>
              <ButtonBase
                data-track='fs-prgMan-active-programs-expand-collapse'
                onClick={() => setShowActivePrograms(!showActivePrograms)}
                style={{ height: 50, flex: 1, minWidth: 0 }}
              >
                <Grid
                  item
                  container
                  alignItems='center'
                  justify='space-between'
                  wrap='nowrap'
                  spacing={1}
                  xs={12}
                  style={{ paddingLeft: 8, paddingRight: 8 }}
                >
                  <ListSubheader style={{ whiteSpace: 'nowrap' }}>Active Programs</ListSubheader>
                  {showActivePrograms ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </Grid>
              </ButtonBase>
              {isVmAdmin && !locked && showActivePrograms && activePrograms.length > 1 && !reorderMode && (
                <Button
                  data-track='fs-prgMan-reorder-programs'
                  size='small'
                  onClick={() => setReorderMode(true)}
                  style={{ textTransform: 'none', marginRight: 8 }}
                >
                  Reorder
                </Button>
              )}
              {reorderMode && (
                <Box display='flex' style={{ gap: 4, marginRight: 8 }}>
                  <Button
                    size='small'
                    onClick={handleCancelReorder}
                    style={{ textTransform: 'none', color: theme.color.grey.neutral300 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    data-track='fs-prgMan-save-program-order'
                    size='small'
                    color='primary'
                    variant='contained'
                    disableElevation
                    disabled={!pendingLayout || hasBlankHeading}
                    onClick={handleSaveOrder}
                    style={{ textTransform: 'none' }}
                  >
                    Save
                  </Button>
                </Box>
              )}
            </Box>
            {activePrograms.length >= 1 ? (
              <Grid item xs={12} style={{ padding: 0 }}>
                <Collapse in={showActivePrograms}>
                  {reorderMode ? (
                    <ProgramGroupsReorderList programs={activePrograms} layout={layout} onChange={setPendingLayout} />
                  ) : (
                    <ProgramsList programs={activePrograms} groups={programGroups} />
                  )}
                </Collapse>
              </Grid>
            ) : activePrograms.length === 0 && isVmAdmin ? (
              <Grid
                item
                xs={12}
                style={{
                  boxSizing: 'border-box',
                  paddingLeft: 24,
                  paddingRight: 24,
                  paddingTop: 16,
                  paddingBottom: 16,
                }}
              >
                <Collapse in={showActivePrograms}>
                  <Typography
                    align='center'
                    variant='body2'
                    color='textSecondary'
                  >{`0 active programs. Hit 'Create program' to add a new program.`}</Typography>
                </Collapse>
              </Grid>
            ) : null}
            <ButtonBase
              data-track='fs-prgMan-suspended-programs-expand-collapse'
              onClick={() => setShowSuspendedPrograms(!showSuspendedPrograms)}
              style={{ height: 50 }}
            >
              <Grid
                item
                container
                alignItems='center'
                justify='space-between'
                spacing={1}
                xs={12}
                style={{ paddingLeft: 8, paddingRight: 8 }}
              >
                <ListSubheader>Suspended programs</ListSubheader>
                {showSuspendedPrograms ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </Grid>
            </ButtonBase>
            {suspendedPrograms.length >= 1 ? (
              <Grid item xs={12} style={{ padding: 0 }}>
                <Collapse in={showSuspendedPrograms}>
                  <ProgramsList programs={suspendedPrograms} />
                </Collapse>
              </Grid>
            ) : suspendedPrograms.length === 0 ? (
              <Grid
                item
                xs={12}
                style={{
                  boxSizing: 'border-box',
                  paddingLeft: 24,
                  paddingRight: 24,
                  paddingTop: 16,
                  paddingBottom: 16,
                }}
              >
                <Collapse in={showSuspendedPrograms}>
                  <Typography align='center' variant='body2' color='textSecondary'>
                    0 suspended programs
                  </Typography>
                </Collapse>
              </Grid>
            ) : null}
          </Grid>
        </Box>
      </Grid>
    </Grid>
  );
};

export { ProgramManagementSideNav };
